/**
 * Endpoints #40-#47 — Save to Notion (SPEC `save-to-notion`).
 *
 * One configured target: one integration token, one data source, one field mapping and any number
 * of fixed values. The token never leaves the server (§10.3) — #40 answers with the `maskSecret`
 * spelling the Scrapers tab already uses.
 *
 * Property **IDs** are the write key and are stored beside their names: a renamed Notion column
 * keeps working, a deleted one is detectable. IDs are opaque, URL-encoded strings and are used
 * verbatim, never decoded.
 */
import { z } from 'zod';
/* ------------------------------------------------------------- property types */
/**
 * Notion property types this app can write into. Read-only types (`formula`, `rollup`,
 * `unique_id`, `created_by`, `created_time`, `last_edited_by`, `last_edited_time`, `verification`)
 * are absent on purpose: Notion errors on a write to most of them and ignores the rest.
 *
 * `people` and `relation` are out of scope — both need ids this app does not have.
 */
export const NOTION_WRITABLE_PROPERTY_TYPES = [
    'title',
    'rich_text',
    'url',
    'email',
    'phone_number',
    'number',
    'checkbox',
    'date',
    'select',
    'multi_select',
    'status',
    'files',
];
/** Types a mapping row or a fixed value may never target — the picker hides them entirely. */
export const NOTION_READONLY_PROPERTY_TYPES = [
    'formula',
    'rollup',
    'unique_id',
    'created_by',
    'created_time',
    'last_edited_by',
    'last_edited_time',
    'verification',
];
const NotionPropertyTypeSchema = z.enum(NOTION_WRITABLE_PROPERTY_TYPES);
const NotionPropertySchema = z.object({
    id: z.string().min(1),
    name: z.string(),
    type: NotionPropertyTypeSchema,
    options: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
});
/* --------------------------------------------------------- source-field catalogue */
/** The mapping UI's rows — one per value this app can hand to a Notion column. */
export const NOTION_SOURCE_FIELDS = [
    'linkedinUrl',
    'appUrl',
    'text',
    'media',
    'postType',
    'contentType',
    'publishedAt',
    'author',
    'reactions',
    'comments',
    'shares',
    'saves',
    'totalEngagement',
    // The §8.3 multipliers, named after the `PostMultipliersDTO` keys they carry.
    'reactionsX',
    'commentsX',
    'sharesX',
    'savesX',
    'totalEngagementX',
    'words',
    'chars',
];
const NotionTargetSchema = z.object({
    propId: z.string().min(1).max(200),
    propName: z.string().max(300),
    propType: NotionPropertyTypeSchema,
});
const NotionFixedInputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), text: z.string().max(4000) }),
    z.object({ kind: z.literal('texts'), texts: z.array(z.string().max(200)).max(100) }),
    z.object({ kind: z.literal('number'), number: z.number().finite() }),
    z.object({ kind: z.literal('checkbox'), checkbox: z.boolean() }),
    z.object({ kind: z.literal('date'), date: z.string().min(1).max(40) }),
    z.object({ kind: z.literal('saveDate') }),
]);
const NotionFixedValueSchema = NotionTargetSchema.extend({ value: NotionFixedInputSchema });
/**
 * Which editor a property type gets, and therefore which input kinds it accepts (SPEC § Fixed
 * values). It lives in the contract package because the Settings tab renders from it and the
 * server validates against it — one table, no chance of the two drifting apart.
 *
 * `title` is derived server-side and `files` is filled by the upload step: neither takes a constant.
 */
export function notionFixedInputKinds(type) {
    switch (type) {
        case 'select':
        case 'status':
        case 'rich_text':
        case 'url':
        case 'email':
        case 'phone_number':
            return ['text'];
        case 'multi_select':
            return ['texts'];
        case 'number':
            return ['number'];
        case 'checkbox':
            return ['checkbox'];
        case 'date':
            return ['date', 'saveDate'];
        case 'title':
        case 'files':
            return [];
    }
}
export function notionSettingsDefault() {
    return {
        token: null,
        databaseId: null,
        dataSourceId: null,
        databaseName: null,
        dataSourceName: null,
        properties: [],
        schemaFetchedAt: null,
        mapping: {},
        fixedValues: [],
        lastSkipped: [],
    };
}
/** Frozen default for the §4.1 registry table; mutable copies come from `notionSettingsDefault`. */
export const NOTION_SETTINGS_DEFAULT = notionSettingsDefault();
/**
 * Lenient decode of the stored blob: every field falls back rather than throwing, so a
 * hand-edited database can never crash a boot (same contract as the other §4.1 validators).
 *
 * `z.object` strips unknown keys, which is what keeps a pre-promotion blob (one that still carries
 * `appBaseUrl` inside it) parsing after the field moved to its own settings key.
 */
const NotionSettingsSchema = z.object({
    token: z.string().nullable().catch(null).default(null),
    databaseId: z.string().nullable().catch(null).default(null),
    dataSourceId: z.string().nullable().catch(null).default(null),
    databaseName: z.string().nullable().catch(null).default(null),
    dataSourceName: z.string().nullable().catch(null).default(null),
    properties: z.array(NotionPropertySchema).catch([]).default([]),
    schemaFetchedAt: z.number().int().nullable().catch(null).default(null),
    mapping: z.partialRecord(z.enum(NOTION_SOURCE_FIELDS), NotionTargetSchema).catch({}).default({}),
    fixedValues: z.array(NotionFixedValueSchema).catch([]).default([]),
    lastSkipped: z.array(z.string()).catch([]).default([]),
});
export function decodeNotionSettings(value) {
    const parsed = NotionSettingsSchema.safeParse(value);
    return parsed.success ? parsed.data : notionSettingsDefault();
}
/* ------------------------------------------------------------------ errors */
/**
 * Classification of a Notion failure (SPEC § Notion HTTP client). Branching is on the Notion
 * `code`, never on its `message` — Notion rewords messages without a version bump.
 */
export const NOTION_ERROR_KINDS = [
    'auth',
    'not_shared',
    'validation',
    'rate_limited',
    'provider_error',
    'timeout',
];
/* ------------------------------------------------------------------ requests */
/** #41 — an empty string clears the key and nothing else. */
export const NotionTokenUpdateBodySchema = z.strictObject({
    token: z.string().trim().max(500),
});
/** #43 — pick a searched data source, or resolve a pasted database URL. */
export const NotionDatabaseUpdateBodySchema = z
    .strictObject({
    dataSourceId: z.string().trim().min(1).max(200).optional(),
    url: z.string().trim().min(1).max(2000).optional(),
})
    .refine((body) => (body.dataSourceId === undefined) !== (body.url === undefined), 'send either dataSourceId or url');
/** #45 — mapping, fixed values and the app base URL commit together. */
export const NotionMappingUpdateBodySchema = z.strictObject({
    mapping: z.partialRecord(z.enum(NOTION_SOURCE_FIELDS), NotionTargetSchema).default({}),
    fixedValues: z.array(NotionFixedValueSchema).max(50).default([]),
    /** Absolute `https://` origin, or an empty string to clear it. */
    appBaseUrl: z.string().trim().max(300).optional(),
});
/** #47 */
export const NotionSaveBodySchema = z.strictObject({
    postId: z.number().int().positive(),
});
/* ------------------------------------------------------------------ constants */
/** Notion API version this build speaks (reference §1). */
export const NOTION_API_VERSION = '2026-03-11';
/** Images uploaded per save (SPEC § Save pipeline step 3). */
export const NOTION_MAX_UPLOADS = 20;
/** Free-plan per-file cap, used until `GET /v1/users/me` reports the workspace's own (7.6). */
export const NOTION_FALLBACK_UPLOAD_LIMIT = 5 * 1024 * 1024;
//# sourceMappingURL=notion.js.map